import React, { useEffect, useState } from 'react';
import styles from './index.module.css';

const getSecondsRemaining = (expiresAt) => {
  if (!expiresAt) return 0;
  return Math.max(0, Math.floor((new Date(expiresAt) - new Date()) / 1000));
};

const formatCountdown = (seconds) => {
  if (seconds >= 86400) {
    const days = Math.floor(seconds / 86400);
    const hours = Math.floor((seconds % 86400) / 3600);
    return `${days}d ${hours}h`;
  }
  if (seconds >= 3600) {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${minutes}m`;
  }
  const m = Math.floor(seconds / 60)
    .toString()
    .padStart(2, '0');
  const s = (seconds % 60).toString().padStart(2, '0');
  return `${m}:${s}`;
};

const Countdown = ({ expiresAt }) => {
  const [seconds, setSeconds] = useState(() => getSecondsRemaining(expiresAt));

  useEffect(() => {
    if (!expiresAt) return undefined;

    setSeconds(getSecondsRemaining(expiresAt));
    const interval = setInterval(() => {
      const remaining = getSecondsRemaining(expiresAt);
      setSeconds(remaining);
      if (remaining === 0) clearInterval(interval);
    }, 1000);

    return () => clearInterval(interval);
  }, [expiresAt]);

  if (!expiresAt) return null;

  return (
    <span className={styles.countdown} aria-live='off'>
      {seconds > 0 ? `Expires in ${formatCountdown(seconds)}` : 'Expired'}
    </span>
  );
};

const PhoneIcon = () => (
  <svg
    width='14'
    height='14'
    viewBox='0 0 256 256'
    fill='currentColor'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden='true'
  >
    <path d='M231.88,175.08A56.26,56.26,0,0,1,176,224C96.6,224,32,159.4,32,80A56.26,56.26,0,0,1,80.92,24.12a16,16,0,0,1,16.62,9.52l21.12,47.15,0,.12A16,16,0,0,1,117.39,96c-.18.27-.37.52-.57.77L96,121.45c7.49,15.22,23.41,31,38.83,38.51l24.34-20.71a8.12,8.12,0,0,1,.75-.56,16,16,0,0,1,15.17-1.4l.13.06,47.11,21.11A16,16,0,0,1,231.88,175.08Z' />
  </svg>
);

const CalendarIcon = () => (
  <svg
    width='14'
    height='14'
    viewBox='0 0 256 256'
    fill='currentColor'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden='true'
  >
    <path d='M208,32H184V24a8,8,0,0,0-16,0v8H88V24a8,8,0,0,0-16,0v8H48A16,16,0,0,0,32,48V208a16,16,0,0,0,16,16H208a16,16,0,0,0,16-16V48A16,16,0,0,0,208,32Zm0,176H48V96H208V208ZM48,80V48H72v8a8,8,0,0,0,16,0V48h80v8a8,8,0,0,0,16,0V48h24V80Z' />
  </svg>
);

const CheckIcon = () => (
  <svg
    width='11'
    height='11'
    viewBox='0 0 256 256'
    fill='currentColor'
    xmlns='http://www.w3.org/2000/svg'
    aria-hidden='true'
  >
    <path d='M229.66,77.66l-128,128a8,8,0,0,1-11.32,0l-56-56a8,8,0,0,1,11.32-11.32L96,188.69,218.34,66.34a8,8,0,0,1,11.32,11.32Z' />
  </svg>
);

const Slot = ({ slotKey, label, onAccept, acceptLabel }) => {
  const interactive = typeof onAccept === 'function';

  const inner = (
    <>
      <span className={styles.slot_main}>
        <span className={styles.slot_icon}>
          <CalendarIcon />
        </span>
        <span className={styles.slot_text}>{label}</span>
      </span>
      {interactive && (
        <span className={styles.slot_accept} aria-hidden='true'>
          <span className={styles.slot_accept_icon}>
            <CheckIcon />
          </span>
          <span className={styles.slot_accept_text}>{acceptLabel}</span>
        </span>
      )}
    </>
  );

  if (interactive) {
    return (
      <button
        type='button'
        key={slotKey}
        onClick={onAccept}
        className={`${styles.slot} ${styles.slot_interactive}`}
        aria-label={`Accept ${label}`}
      >
        {inner}
      </button>
    );
  }

  return (
    <div key={slotKey} className={styles.slot}>
      {inner}
    </div>
  );
};

const TONE_CLASS = {
  waiting: styles.tone_waiting,
  accepted: styles.tone_accepted,
  declined: styles.tone_declined,
};

const CallEventCard = ({
  title = 'Waiting for confirmation',
  description,
  actionLink,
  slots = [],
  tone = 'waiting',
  icon,
  acceptLabel = 'Tap to accept',
  onCancel,
  cancelLabel = 'Cancel call',
  cancelConfirmTitle = 'Cancel this call?',
  cancelConfirmYesLabel = 'Yes, cancel',
  cancelConfirmNoLabel = 'Keep it',
  isCanceling = false,
  defaultConfirmingCancel = false,
  onCounter,
  counterLabel = 'Propose new times',
  onAccept,
  acceptButtonLabel = 'Accept call',
  secondaryAction,
  expiresAt,
}) => {
  const [confirmingCancel, setConfirmingCancel] = useState(defaultConfirmingCancel);
  const toneClass = TONE_CLASS[tone] || TONE_CLASS.waiting;
  const cancellable = typeof onCancel === 'function';
  const counterable = typeof onCounter === 'function';
  const acceptable = typeof onAccept === 'function';
  const hasSecondaryAction = typeof secondaryAction?.onClick === 'function' && !!secondaryAction?.label;

  useEffect(() => {
    if (!cancellable) {
      setConfirmingCancel(false);
    }
  }, [cancellable]);

  if (confirmingCancel) {
    return (
      <div className={styles.card_wrap}>
        <div className={`${styles.card} ${toneClass}`}>
          <div className={styles.header}>
            <span className={styles.header_icon}>{icon || <PhoneIcon />}</span>
            <span className={styles.title}>{cancelConfirmTitle}</span>
          </div>
          <div className={styles.body}>
            <div className={styles.confirm_actions}>
              <button type='button' onClick={onCancel} disabled={isCanceling} className={styles.confirm_yes_btn}>
                {isCanceling ? 'Canceling…' : cancelConfirmYesLabel}
              </button>
              <button
                type='button'
                onClick={() => setConfirmingCancel(false)}
                disabled={isCanceling}
                className={styles.confirm_no_btn}
              >
                {cancelConfirmNoLabel}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const scheduleLinkable =
    actionLink &&
    typeof actionLink.onClick === 'function' &&
    typeof actionLink.label === 'string' &&
    actionLink.label.length > 0;
  const hasBody =
    description ||
    scheduleLinkable ||
    slots.length > 0 ||
    cancellable ||
    counterable ||
    acceptable ||
    hasSecondaryAction;

  return (
    <div className={styles.card_wrap}>
      <div className={`${styles.card} ${toneClass}`}>
        <div className={styles.header}>
          <span className={styles.header_icon}>{icon || <PhoneIcon />}</span>
          <span className={styles.title}>{title}</span>
          {expiresAt && <Countdown expiresAt={expiresAt} />}
        </div>

        {hasBody && (
          <div className={styles.body}>
            {description && <p className={styles.description}>{description}</p>}
            {scheduleLinkable && (
              <button type='button' onClick={actionLink.onClick} className={styles.action_link}>
                {actionLink.label}
              </button>
            )}
            {slots.length > 0 && (
              <div className={styles.slots}>
                {slots.map((slot, index) => (
                  <Slot
                    key={slot.key || slot.label || index}
                    slotKey={slot.key || slot.label || index}
                    label={slot.label}
                    onAccept={slot.onAccept}
                    acceptLabel={slot.acceptLabel || acceptLabel}
                  />
                ))}
              </div>
            )}
            {(acceptable || cancellable || counterable || hasSecondaryAction) && (
              <div className={styles.actions}>
                {acceptable && (
                  <button type='button' onClick={onAccept} className={styles.accept_btn}>
                    {acceptButtonLabel}
                  </button>
                )}
                {hasSecondaryAction && (
                  <button type='button' onClick={secondaryAction.onClick} className={styles.counter_btn}>
                    {secondaryAction.label}
                  </button>
                )}
                {cancellable && (
                  <button type='button' onClick={() => setConfirmingCancel(true)} className={styles.cancel_btn}>
                    {cancelLabel}
                  </button>
                )}
                {counterable && (
                  <button type='button' onClick={onCounter} className={styles.counter_btn}>
                    {counterLabel}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CallEventCard;
